import { useApiQuery } from '@locus-taxy/encore-ui-network';
import type { EntityDetailsAction } from '../../types/actions';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import type { ApiError } from '@locus-taxy/encore-ui-network';
import { useSearchParams } from 'react-router-dom';

export interface EntityDetailsResponse {
  data: {
    actions: EntityDetailsAction[];
    data: {
      id: string;
      entityData: Record<string, any>;
      _timelineAggregations?: Record<string, any>;
      schemaInfo?: any;
      metadata?: any;
      createdAt?: string;
      updatedAt?: string;
      createdBy?: string;
      updatedBy?: string;
      version?: number;
      deleted?: boolean;
      [key: string]: any;
    };
    timeline?: {
      entries: unknown[];
    };
    [key: string]: any;
  };
  success: boolean;
}

function buildParams(
  clientId: string,
  entityName: string,
  instanceId: string | null,
  fields?: string[],
  includeRelations?: boolean,
  parentId?: string | null
) {
  return {
    clientId,
    entityName,
    instanceId: instanceId ?? '',
    ...(fields && { fields: fields.join(',') }),
    ...(includeRelations !== undefined && {
      includeRelations: String(includeRelations),
    }),
    ...(parentId && { parentId }),
  };
}

export function useEntityDetails(
  clientId: string,
  entityName: string,
  instanceId: string | null,
  fields?: string[],
  includeRelations?: boolean,
  options?: { enabled?: boolean }
) {
  const { showApiError } = useToast();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get('parentId');

  const baseEnabled = Boolean(clientId && entityName && instanceId);
  const isEnabled = (options?.enabled ?? true) && baseEnabled;

  return useApiQuery<EntityDetailsResponse>({
    key: [
      'entityDetails',
      clientId,
      entityName,
      instanceId ?? '',
      String(includeRelations ?? false),
      parentId ?? '',
    ],
    endpointKey: 'entity.details',
    config: {
      enabled: isEnabled,
      staleTime: 0,
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
    params: buildParams(
      clientId,
      entityName,
      instanceId,
      fields,
      includeRelations,
      parentId
    ),
  });
}
